import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getContacts, markContactRead, replyContact, deleteContact } from '../../lib/api/adminApi';

const SUPPORT_EMAIL = 'support@keepmygames.com';

function AdminMessages({ onAuthError }) {
  const { t } = useTranslation();
  const [list, setList] = useState([]);
  const [selected, setSelected] = useState(null);
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const [flash, setFlash] = useState(null); // { type: 'ok'|'err', msg }
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    getContacts()
      .then(({ data }) => setList(data || []))
      .catch((err) => {
        if (onAuthError) onAuthError(err);
        setList([]);
      })
      .finally(() => setLoading(false));
  }, [onAuthError]);

  useEffect(() => {
    load();
  }, [load]);

  const open = (m) => {
    setSelected(m);
    setReply('');
    setFlash(null);
    if (!m.isRead) {
      markContactRead(m.id)
        .then(() => setList((l) => l.map((x) => (x.id === m.id ? { ...x, isRead: true } : x))))
        .catch((err) => onAuthError && onAuthError(err));
    }
  };

  const send = async (e) => {
    e.preventDefault();
    if (!reply.trim()) return;
    setSending(true);
    setFlash(null);
    try {
      await replyContact(selected.id, reply);
      const repliedAt = new Date().toISOString();
      setList((l) => l.map((x) => (x.id === selected.id ? { ...x, repliedAt, isRead: true } : x)));
      setSelected((s) => ({ ...s, repliedAt }));
      setReply('');
      setFlash({ type: 'ok', msg: t('admin.msg_reply_sent') });
    } catch (err) {
      if (!(onAuthError && onAuthError(err))) setFlash({ type: 'err', msg: t('admin.msg_reply_error') });
    } finally {
      setSending(false);
    }
  };

  const remove = async (m) => {
    if (!window.confirm(t('admin.msg_delete_confirm'))) return;
    try {
      await deleteContact(m.id);
      setList((l) => l.filter((x) => x.id !== m.id));
      if (selected && selected.id === m.id) setSelected(null);
    } catch (err) {
      onAuthError && onAuthError(err);
    }
  };

  const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString() : '');
  const unreadCount = list.filter((m) => !m.isRead).length;

  return (
    <div className="msgs">
      <div className="msgs__list">
        <div className="msgs__list-head">
          {t('admin.msg_inbox')} · {list.length}
          {unreadCount > 0 && <span className="msgs__unread-count">{unreadCount}</span>}
        </div>
        {loading ? (
          <p className="admin__empty">…</p>
        ) : list.length === 0 ? (
          <p className="admin__empty">{t('admin.msg_empty')}</p>
        ) : (
          <ul className="msgs__ul">
            {list.map((m) => (
              <li
                key={m.id}
                className={`msgs__item ${selected && selected.id === m.id ? 'is-active' : ''} ${
                  m.isRead ? '' : 'is-unread'
                }`}
                onClick={() => open(m)}
              >
                <div className="msgs__item-top">
                  <span className="msgs__item-from">{m.nom || m.mail}</span>
                  <span className="msgs__item-date">{fmtDate(m.createdAt)}</span>
                </div>
                <div className="msgs__item-subject">{m.sujet || '—'}</div>
                <div className="msgs__item-badges">
                  {!m.isRead && <span className="msgs__badge msgs__badge--new">{t('admin.msg_new')}</span>}
                  {m.repliedAt && <span className="msgs__badge msgs__badge--replied">{t('admin.msg_replied')}</span>}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="msgs__detail">
        {!selected ? (
          <p className="admin__empty msgs__placeholder">{t('admin.msg_select')}</p>
        ) : (
          <>
            <div className="msgs__detail-head">
              <div>
                <div className="msgs__detail-subject">{selected.sujet || '—'}</div>
                <div className="msgs__detail-meta">
                  {selected.nom && <strong>{selected.nom}</strong>}{' '}
                  <a href={`mailto:${selected.mail}`}>{selected.mail}</a> · {fmtDate(selected.createdAt)}
                </div>
              </div>
              <button className="msgs__delete" onClick={() => remove(selected)} title={t('admin.delete')}>
                ✕
              </button>
            </div>

            <div className="msgs__body">{selected.message}</div>

            {selected.repliedAt && (
              <div className="msgs__already-replied">
                {t('admin.msg_already_replied', { date: fmtDate(selected.repliedAt) })}
              </div>
            )}

            <form className="msgs__reply" onSubmit={send}>
              <div className="msgs__reply-title">{t('admin.msg_reply_title')}</div>
              <div className="msgs__reply-row">
                <span className="msgs__reply-label">{t('admin.msg_from')}</span>
                <span className="msgs__reply-fixed">{SUPPORT_EMAIL}</span>
              </div>
              <div className="msgs__reply-row">
                <span className="msgs__reply-label">{t('admin.msg_to')}</span>
                <span className="msgs__reply-fixed">{selected.mail}</span>
              </div>
              <div className="msgs__reply-row">
                <span className="msgs__reply-label">{t('admin.msg_subject')}</span>
                <span className="msgs__reply-fixed">Re: {selected.sujet || ''}</span>
              </div>
              {flash && (
                <div className={`contact__flash contact__flash--${flash.type === 'ok' ? 'ok' : 'err'}`}>
                  {flash.msg}
                </div>
              )}
              <textarea
                className="msgs__reply-text"
                rows="6"
                placeholder={t('admin.msg_reply_placeholder')}
                value={reply}
                onChange={(e) => setReply(e.target.value)}
                required
              />
              <button type="submit" className="btn btn--amber msgs__reply-send" disabled={sending}>
                {sending ? '…' : t('admin.msg_send')}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}

export default AdminMessages;
